import { CopyOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { type MouseEvent, type ReactNode } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useMessage } from 'components/ui';

const ButtonWrapper = styled.span<{ $compact?: boolean }>`
  display: ${({ $compact }) => ($compact ? 'inline-flex' : 'inline')};
  flex-shrink: ${({ $compact }) => ($compact ? 0 : 'initial')};
  margin-top: ${({ $compact }) => ($compact ? '0.15em' : '0')};
  margin-left: ${({ $compact }) => ($compact ? '0' : '0.5em')};
`;

const CompactCopyButton = styled(Button)`
  && {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  &&:hover,
  &&:focus {
    background: transparent !important;
  }
`;

type Props = {
  children: ReactNode;
  title?: string;
  value: string;
  compact?: boolean;
};

export default function CopyClipboard({ children, title, value, compact = false }: Props) {
  const { t } = useTranslation();
  const message = useMessage();
  const tooltipTitle = title ?? t('common.copyToClipboard', 'Copy to Clipboard');

  return (
    <>
      {children}

      <ButtonWrapper $compact={compact}>
        <CopyToClipboard
          text={value}
          onCopy={() => message.success(t('notifications.success.copied', 'Copied to clipboard'))}
        >
          <Tooltip title={tooltipTitle}>
            {compact ? (
              <CompactCopyButton
                type="text"
                size="small"
                aria-label={tooltipTitle}
                icon={<CopyOutlined />}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              />
            ) : (
              <Button size="large" aria-label={tooltipTitle}>
                <CopyOutlined />
              </Button>
            )}
          </Tooltip>
        </CopyToClipboard>
      </ButtonWrapper>
    </>
  );
}
